/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-array-index-key */
/* eslint-disable @typescript-eslint/no-restricted-imports */
// MegaMenuEditor.tsx - WITH COPY/PASTE AND MOVE FUNCTIONALITY
import { CustomerDocument, useCustomerQuery } from '@graphcommerce/magento-customer'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Button,
  IconButton,
  CircularProgress,
} from '@mui/material'
import { useRouter } from 'next/router'
import React, { useContext, useState, useRef, useEffect } from 'react'
import ReactDOM from 'react-dom'
import { uploadToGCS } from '../../hooks/uploadToGCS'
import { Icon } from '../Layout/Icon'
import { globalContext } from '../NextUi/globalContext'
import { AlertPrompt } from '../Prompt/AlertPrompt'
import { MegaMenuItem } from './NewMegaMenuHeaderTabs'

// Image Upload Section Component
interface ImageUploadSectionProps {
  itemName: string
  onImageUploaded: (url: string) => void
}

const ImageUploadSection: React.FC<ImageUploadSectionProps> = ({ itemName, onImageUploaded }) => {
  const [uploading, setUploading] = useState(false)
  const [uploadSuccess, setUploadSuccess] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const dashboard = useCustomerQuery(CustomerDocument, {
    fetchPolicy: 'cache-and-network',
  })
  const customer = dashboard.data?.customer

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file')
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('Image must be smaller than 5MB')
        return
      }
      setSelectedFile(file)
      setUploadSuccess(false)
    }
  }

  const handleUpload = async () => {
    if (!selectedFile) return
    setUploading(true)
    setUploadSuccess(false)

    try {
      // Get user email from your context or props
      const userEmail = customer?.email // From your Magento customer

      if (!userEmail) {
        throw new Error('User email not found')
      }

      // Step 2: Upload to GCS with JWT token
      const publicUrl = await uploadToGCS(selectedFile, userEmail)

      // Step 3: Update the image URL in your component
      onImageUploaded(publicUrl)

      setUploadSuccess(true)
      setSelectedFile(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }

      setTimeout(() => setUploadSuccess(false), 2000)
    } catch (error) {
      console.error('Upload error:', error)

      if (error instanceof Error) {
        if (error.message.includes('permission')) {
          alert('You do not have permission to upload images')
        } else {
          alert(`Failed to upload image: ${error.message}`)
        }
      } else {
        alert('Failed to upload image')
      }
    } finally {
      setUploading(false)
    }
  }

  const handleClearSelection = () => {
    setSelectedFile(null)
    setUploadSuccess(false)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <div className='rounded border border-20-grey p-3'>
      <p className='mb-2 text-xs font-medium text-60-grey'>Upload New Image</p>
      <input
        ref={fileInputRef}
        type='file'
        accept='image/*'
        onChange={handleFileSelect}
        className='hidden'
        id={`file-upload-${itemName}`}
      />
      <div className='space-y-2'>
        <label htmlFor={`file-upload-${itemName}`}>
          <Button
            component='span'
            variant='outlined'
            fullWidth
            size='small'
            startIcon={<Icon name='image' className='text-[14px]' />}
            disabled={uploading}
            sx={{
              borderColor: '#D1D3D4',
              color: '#111111',
              '&:hover': { borderColor: '#A95CFF', backgroundColor: '#F4E9FF' },
            }}
          >
            {selectedFile ? 'Change Image' : 'Select Image'}
          </Button>
        </label>
        {selectedFile && (
          <div className='flex items-center justify-between rounded bg-5-grey p-2'>
            <div className='flex min-w-0 flex-1 items-center gap-2'>
              <Icon name='image' className='flex-shrink-0 text-[16px] text-bright-templi-purple' />
              <span className='truncate text-xs text-tight-black'>{selectedFile.name}</span>
            </div>
            <IconButton
              size='small'
              onClick={handleClearSelection}
              disabled={uploading}
              sx={{ padding: '2px', color: '#6D6E71', '&:hover': { backgroundColor: '#F1F2F2' } }}
            >
              <Icon name='close' className='text-[16px]' />
            </IconButton>
          </div>
        )}
        <Button
          variant='contained'
          fullWidth
          size='small'
          onClick={handleUpload}
          disabled={!selectedFile || uploading}
          startIcon={
            uploading ? (
              <CircularProgress size={14} sx={{ color: '#FFFFFF' }} />
            ) : uploadSuccess ? (
              <Icon name='check_circle' className='text-[14px]' />
            ) : (
              <Icon name='cloud_upload' className='text-[14px]' />
            )
          }
          sx={{
            border: 1,
            borderColor: '#A95CFF',
            color: '#A95CFF',
            backgroundColor: '#FFFFFF',
            '&:hover': { backgroundColor: '#884ACD', color: '#FFFFFF' },
            '&:disabled': { backgroundColor: '#939598', color: '#FFFFFF', border: 0 },
          }}
        >
          {uploading ? 'Uploading...' : uploadSuccess ? 'Uploaded!' : 'Upload Image'}
        </Button>
        <p className='text-center text-[10px] text-50-grey'>Max 5MB • JPG, PNG, GIF, WebP</p>
      </div>
    </div>
  )
}

interface MenuItemEditorProps {
  item: MegaMenuItem
  path: number[]
  onUpdate: (path: number[], updates: Partial<MegaMenuItem>) => void
  onDelete: (path: number[]) => void
  onAddChild: (path: number[]) => void
  onCopy: (path: number[]) => void
  onCut: (path: number[]) => void
  onPaste: (path: number[]) => void
  onMoveUp: (path: number[]) => void
  onMoveDown: (path: number[]) => void
  level?: number
  isLast?: boolean
  isFirst?: boolean
  hasClipboard?: boolean
}

const MenuItemEditor: React.FC<MenuItemEditorProps> = ({
  item,
  path,
  onUpdate,
  onDelete,
  onAddChild,
  onCopy,
  onCut,
  onPaste,
  onMoveUp,
  onMoveDown,
  level = 0,
  isLast = false,
  isFirst = false,
  hasClipboard = false,
}) => {
  const [localItem, setLocalItem] = useState<MegaMenuItem>(item)
  const [hasChanges, setHasChanges] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)

  // Sync localItem when item prop changes (e.g., when children are added via paste)
  useEffect(() => {
    if (!hasChanges) {
      setLocalItem(item)
    }
  }, [item, hasChanges])

  const handleFieldChange = (field: keyof MegaMenuItem, value: any) => {
    setLocalItem((prev) => ({ ...prev, [field]: value }))
    setHasChanges(true)
  }

  const handleSave = () => {
    onUpdate(path, localItem)
    setHasChanges(false)
  }

  const handleReset = () => {
    setLocalItem(item)
    setHasChanges(false)
  }

  if (level > 4) {
    return (
      <div className='rounded border border-20-grey p-2 text-xs text-60-grey'>
        ⚠️ Max nesting level reached
      </div>
    )
  }

  const getBackgroundColor = () => {
    switch (level) {
      case 0:
        return 'bg-lighter-templi-purple'
      case 1:
        return 'bg-lightest-purple'
      case 2:
        return 'bg-templi-7-purple'
      default:
        return 'bg-pure-white'
    }
  }

  const getBorderColor = () =>
    localItem.isCategory
      ? '!border-l-4 !border-[1px] !border-l-bright-templi-purple !border-light-gray'
      : '!border-[1px] !border-light-gray'

  const hasChildren = localItem.children && localItem.children.length > 0

  return (
    <div className={`relative mb-2 ${level > 0 ? 'ml-2 pl-1 md:ml-8 md:pl-4' : ''}`}>
      {level > 0 && (
        <>
          {!isLast && (
            <div
              className='absolute bottom-0 left-0 top-0 hidden w-[2px] bg-20-grey md:block'
              style={{ left: '-16px' }}
            />
          )}
          <div
            className='absolute top-0 hidden w-[2px] bg-20-grey md:block'
            style={{ left: '-16px', height: '32px' }}
          />
          <div
            className='absolute top-8 hidden h-[2px] w-4 bg-20-grey md:block'
            style={{ left: '-16px' }}
          />
          <div
            className='absolute left-[-16px] top-8 hidden h-2 w-2 rounded-full bg-bright-templi-purple md:block'
            style={{ transform: 'translate(-50%, -50%)' }}
          />
        </>
      )}

      <Accordion
        expanded={isExpanded}
        onChange={() => setIsExpanded(!isExpanded)}
        className={`${getBackgroundColor()} ${getBorderColor()} shadow-sm`}
        sx={{ '&:before': { display: 'none' }, boxShadow: '0 1px 3px rgba(169, 92, 255, 0.1)' }}
      >
        <AccordionSummary
          expandIcon={<Icon name='expand_more' className='text-bright-templi-purple' />}
          className='px-3 transition-colors hover:bg-lighter-templi-purple md:px-5'
          sx={{
            px: 1.5,
            md: {
              px: 2.5,
            },
          }}
        >
          <div className='flex w-full flex-col justify-between gap-2 md:flex-row md:items-center md:gap-0 md:pr-4'>
            <div className='flex flex-wrap items-center gap-2 md:gap-3'>
              {localItem.isCategory && (
                <span className='rounded bg-bright-templi-purple px-2 py-1 text-xs font-medium text-pure-white'>
                  Category
                </span>
              )}
              <span className='text-sm font-semibold text-tight-black md:text-base'>
                {localItem.name || '(Unnamed)'}
              </span>
              {localItem.moq && <span className='text-xs text-60-grey'>MOQ: {localItem.moq}</span>}
              {hasChildren && (
                <span className='text-xs text-50-grey'>
                  ({localItem.children.length}{' '}
                  {localItem.children.length === 1 ? 'child' : 'children'})
                </span>
              )}
            </div>
            {hasChanges && (
              <span className='self-start rounded bg-marigold px-2 py-1 text-xs text-tight-black md:self-auto'>
                Unsaved
              </span>
            )}
          </div>
        </AccordionSummary>

        <AccordionDetails className='bg-pure-white'>
          <div className='space-y-4'>
            <TextField
              label='Name'
              value={localItem.name || ''}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              fullWidth
              variant='outlined'
              size='small'
            />
            <TextField
              label='URL'
              value={localItem.url || ''}
              onChange={(e) => handleFieldChange('url', e.target.value)}
              fullWidth
              variant='outlined'
              size='small'
              placeholder='https://www.templi.com/...'
            />
            <TextField
              label='Image URL'
              value={localItem.image_url || ''}
              onChange={(e) => handleFieldChange('image_url', e.target.value)}
              fullWidth
              variant='outlined'
              size='small'
              placeholder='https://storage.googleapis.com/...'
            />

            <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
              <div className='rounded border border-20-grey p-3'>
                <p className='mb-2 text-xs font-medium text-60-grey'>Image Preview</p>
                {localItem.image_url ? (
                  <img
                    src={localItem.image_url}
                    alt={localItem.name}
                    className='max-h-32 w-full rounded bg-5-grey object-contain'
                    onError={(e) => {
                      e.currentTarget.src =
                        'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="128"%3E%3Crect fill="%23F1F2F2" width="200" height="128"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%236D6E71" font-size="12"%3EImage not found%3C/text%3E%3C/svg%3E'
                    }}
                  />
                ) : (
                  <div className='flex h-32 items-center justify-center rounded bg-5-grey'>
                    <span className='text-xs text-50-grey'>No image</span>
                  </div>
                )}
              </div>
              <ImageUploadSection
                itemName={localItem.name}
                onImageUploaded={(url: string) => handleFieldChange('image_url', url)}
              />
            </div>

            {!localItem.isCategory && (
              <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                <TextField
                  label='MOQ'
                  value={localItem.moq || ''}
                  onChange={(e) => handleFieldChange('moq', e.target.value)}
                  fullWidth
                  variant='outlined'
                  size='small'
                />
                <TextField
                  label='Lead Time'
                  value={localItem.production_time || ''}
                  onChange={(e) => handleFieldChange('production_time', e.target.value)}
                  fullWidth
                  variant='outlined'
                  size='small'
                />
              </div>
            )}

            <div className='flex gap-6 rounded bg-lightest-gray p-3'>
              <label className='flex cursor-pointer items-center gap-2'>
                <input
                  type='checkbox'
                  checked={localItem.isCategory}
                  onChange={(e) => handleFieldChange('isCategory', e.target.checked)}
                  className='h-4 w-4 cursor-pointer accent-bright-templi-purple'
                />
                <span className='text-sm text-tight-black'>Is Category</span>
              </label>
              <label className='flex cursor-pointer items-center gap-2'>
                <input
                  type='checkbox'
                  checked={localItem.free_shipping || false}
                  onChange={(e) => handleFieldChange('free_shipping', e.target.checked)}
                  className='h-4 w-4 cursor-pointer accent-bright-templi-purple'
                />
                <span className='text-sm text-tight-black'>Free Shipping</span>
              </label>
            </div>

            <TextField
              label='ID'
              value={localItem.id || 'null'}
              fullWidth
              variant='outlined'
              size='small'
              disabled
              helperText='Auto-generated or null'
              sx={{ '& .MuiInputLabel-root': { color: '#939598' } }}
            />

            {/* Action Buttons */}
            <div className='space-y-2 border-t border-20-grey pt-2'>
              {/* Row 1: Save, Reset, Copy, Cut, Paste */}
              <div className='flex flex-wrap gap-2'>
                <Button
                  variant='contained'
                  startIcon={<Icon name='save' className='text-[14px]' />}
                  onClick={handleSave}
                  disabled={!hasChanges}
                  size='small'
                  sx={{
                    border: 1,
                    borderColor: '#A95CFF',
                    color: '#A95CFF',
                    backgroundColor: '#FFFFFF',
                    '&:hover': { backgroundColor: '#884ACD', color: '#FFFFFF' },
                    '&:disabled': { backgroundColor: '#939598', color: '#FFFFFF', border: 0 },
                  }}
                >
                  Save
                </Button>

                {hasChanges && (
                  <Button
                    variant='outlined'
                    onClick={handleReset}
                    size='small'
                    sx={{
                      borderColor: '#A95CFF',
                      color: '#A95CFF',
                      '&:hover': { borderColor: '#A95CFF', backgroundColor: '#F4E9FF' },
                    }}
                  >
                    Reset
                  </Button>
                )}

                <IconButton
                  onClick={() => {
                    console.log('Copy clicked, path:', path)
                    onCopy(path)
                  }}
                  size='small'
                  title='Copy'
                  sx={{ color: '#A95CFF', '&:hover': { backgroundColor: '#F4E9FF' } }}
                >
                  <Icon name='content_copy' className='text-[18px]' />
                </IconButton>

                <IconButton
                  onClick={() => {
                    console.log('Cut clicked, path:', path)
                    onCut(path)
                  }}
                  size='small'
                  title='Cut'
                  sx={{ color: '#A95CFF', '&:hover': { backgroundColor: '#F4E9FF' } }}
                >
                  <Icon name='content_cut' className='text-[18px]' />
                </IconButton>

                {hasClipboard && (
                  <IconButton
                    onClick={() => {
                      console.log('Paste clicked, path:', path)
                      onPaste(path)
                    }}
                    size='small'
                    title='Paste as child'
                    sx={{ color: '#26834B', '&:hover': { backgroundColor: '#D1FFBE' } }}
                  >
                    <Icon name='content_paste' className='text-[18px]' />
                  </IconButton>
                )}

                <div className='flex-1' />

                <IconButton
                  onClick={() => {
                    console.log('Move up clicked, path:', path)
                    onMoveUp(path)
                  }}
                  disabled={isFirst}
                  size='small'
                  title='Move up'
                  sx={{
                    color: '#A95CFF',
                    '&:hover': { backgroundColor: '#F4E9FF' },
                    '&:disabled': { color: '#D1D3D4' },
                  }}
                >
                  <Icon name='arrow_upward' className='text-[18px]' />
                </IconButton>

                <IconButton
                  onClick={() => {
                    console.log('Move down clicked, path:', path)
                    onMoveDown(path)
                  }}
                  disabled={isLast}
                  size='small'
                  title='Move down'
                  sx={{
                    color: '#A95CFF',
                    '&:hover': { backgroundColor: '#F4E9FF' },
                    '&:disabled': { color: '#D1D3D4' },
                  }}
                >
                  <Icon name='arrow_downward' className='text-[18px]' />
                </IconButton>
              </div>

              {/* Row 2: Add Child, Delete */}
              <div className='flex gap-2'>
                <Button
                  variant='outlined'
                  startIcon={<Icon name='add' className='text-[14px]' />}
                  onClick={() => onAddChild(path)}
                  size='small'
                  className='flex-1'
                  sx={{
                    border: 1,
                    borderColor: '#A95CFF',
                    color: '#A95CFF',
                    backgroundColor: '#FFFFFF',
                    '&:hover': { borderColor: '#A95CFF', backgroundColor: '#F4E9FF' },
                  }}
                >
                  Add Child
                </Button>

                <IconButton
                  onClick={() => {
                    if (window.confirm(`Delete "${localItem.name}" and all its children?`)) {
                      onDelete(path)
                    }
                  }}
                  size='small'
                  sx={{ color: '#DF1615', '&:hover': { backgroundColor: '#FFF5F5' } }}
                >
                  <Icon name='delete' className='text-[18px]' />
                </IconButton>
              </div>
            </div>
          </div>
        </AccordionDetails>
      </Accordion>

      {isExpanded && hasChildren && (
        <div className='mt-2'>
          {localItem.children.map((child, index) => (
            <MenuItemEditor
              key={`${path.join('-')}-${index}-${child.name}`}
              item={child}
              path={[...path, index]}
              onUpdate={onUpdate}
              onDelete={onDelete}
              onAddChild={onAddChild}
              onCopy={onCopy}
              onCut={onCut}
              onPaste={onPaste}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              level={level + 1}
              isLast={index === localItem.children.length - 1}
              isFirst={index === 0}
              hasClipboard={hasClipboard}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export function MegaMenuEditor() {
  const { updateMenuItems, menuItems } = useContext(globalContext)
  const [editedMenuItems, setEditedMenuItems] = useState<MegaMenuItem[]>(() => menuItems || [])
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [displayPrompt, setDisplayPrompt] = useState<boolean>(false)
  const [clipboard, setClipboard] = useState<{
    item: MegaMenuItem
    mode: 'copy' | 'cut'
    sourcePath: number[]
  } | null>(null)
  const hasInitializedRef = useRef(false)
  const componentIdRef = useRef(Math.random().toString(36).substring(7))
  const router = useRouter()
  const dashboard = useCustomerQuery(CustomerDocument, {
    fetchPolicy: 'cache-and-network',
  })
  const customer = dashboard.data?.customer
  const { loading } = dashboard
  const hasLoadedRef = useRef(false) // ✅ Track if query has loaded at least once

  // Check if component is re-mounting
  useEffect(() => {
    console.log('🎬 COMPONENT MOUNTED - ID:', componentIdRef.current)
    return () => {
      console.log('💀 COMPONENT UNMOUNTING - ID:', componentIdRef.current)
      console.log('⚠️ WARNING: Component unmounted! This will clear all state!')
    }
  }, [])

  // Auto-hide message snackbar after 3 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage(null)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [message])

  // Sync with context ONLY on initial load - using ref to avoid re-renders
  useEffect(() => {
    console.log('=== useEffect FIRED === Component ID:', componentIdRef.current)
    console.log('menuItems from context:', menuItems)
    console.log('menuItems length:', menuItems?.length)
    console.log('hasInitializedRef.current:', hasInitializedRef.current)
    console.log('editedMenuItems length:', editedMenuItems.length)

    // Only initialize once when menuItems first becomes available
    if (menuItems && menuItems.length > 0 && !hasInitializedRef.current) {
      console.log('✅ Initial sync: loading menuItems from context')
      setEditedMenuItems(menuItems)
      hasInitializedRef.current = true
    } else {
      console.log('❌ Skipping sync')
      console.log('  - menuItems exists:', !!menuItems)
      console.log('  - menuItems length:', menuItems?.length)
      console.log('  - hasInitialized:', hasInitializedRef.current)
      console.log('  - editedMenuItems length:', editedMenuItems.length)
    }
    console.log('=== useEffect END ===')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuItems]) // ✅ Only watch menuItems from context

  useEffect(() => {
    // ✅ Only redirect after the initial load is complete
    if (hasLoadedRef.current && !customer) {
      alert('You need to be logged in to view this page')
      router.push('/')
    }
    // ✅ Mark as loaded once loading completes
    if (!loading && !hasLoadedRef.current) {
      hasLoadedRef.current = true
    }
  }, [customer, loading, router])

  // ✅ Show loading during initial fetch
  if (loading && !hasLoadedRef.current) {
    return (
      <div className='flex h-screen w-screen items-center justify-center'>
        <div className='text-center'>
          <CircularProgress sx={{ color: '#A95CFF' }} />
          <p className='mt-4 text-60-grey'>Loading...</p>
        </div>
      </div>
    )
  }

  // ✅ Show redirecting only after we know there's no customer
  if (!customer) {
    return <div className='flex h-screen w-screen items-center justify-center'>Redirecting...</div>
  }

  const handleUpdateItem = (path: number[], updates: Partial<MegaMenuItem>) => {
    setEditedMenuItems((prevItems) => {
      const newItems = JSON.parse(JSON.stringify(prevItems))
      let current: any = newItems
      for (let i = 0; i < path.length - 1; i++) {
        current = current[path[i]].children
      }
      const itemIndex = path[path.length - 1]
      current[itemIndex] = { ...current[itemIndex], ...updates }
      return newItems
    })
  }

  const handleDeleteItem = (path: number[]) => {
    setEditedMenuItems((prevItems) => {
      const newItems = JSON.parse(JSON.stringify(prevItems))
      let current: any = newItems
      for (let i = 0; i < path.length - 1; i++) {
        current = current[path[i]].children
      }
      current.splice(path[path.length - 1], 1)
      return newItems
    })
    if (clipboard && pathsEqual(clipboard.sourcePath, path)) {
      setClipboard(null)
    }
  }

  const handleAddChild = (path: number[]) => {
    setEditedMenuItems((prevItems) => {
      const newItems = JSON.parse(JSON.stringify(prevItems))
      let current: any = newItems
      for (let i = 0; i < path.length; i++) {
        if (i === path.length - 1) {
          current = current[path[i]]
        } else {
          current = current[path[i]].children
        }
      }
      const newChild: MegaMenuItem = {
        name: 'New Item',
        id: null,
        image_url: null,
        moq: null,
        production_time: null,
        free_shipping: false,
        isCategory: false,
        url: null,
        children: [],
      }
      if (!current.children) current.children = []
      current.children.push(newChild)
      return newItems
    })
  }

  const pathsEqual = (path1: number[], path2: number[]) => {
    if (path1.length !== path2.length) return false
    return path1.every((val, idx) => val === path2[idx])
  }

  const getItemByPath = (items: MegaMenuItem[], path: number[]): MegaMenuItem | null => {
    let current: any = items
    for (let i = 0; i < path.length; i++) {
      if (!current[path[i]]) return null
      if (i === path.length - 1) return current[path[i]]
      current = current[path[i]].children || []
    }
    return null
  }

  const handleCopyItem = (path: number[]) => {
    console.log('=== COPY START ===')
    console.log('Copy called with path:', path)
    console.log('Current editedMenuItems:', editedMenuItems)

    const item = getItemByPath(editedMenuItems, path)
    console.log('Item found:', item)

    if (item) {
      const clipboardData = {
        item: JSON.parse(JSON.stringify(item)),
        mode: 'copy' as const,
        sourcePath: path,
      }
      console.log('Setting clipboard to:', clipboardData)
      setClipboard(clipboardData)
      setMessage({ type: 'success', text: `"${item.name}" copied` })
      console.log('=== COPY END ===')
    } else {
      console.error('Item not found at path:', path)
    }
  }

  const handleCutItem = (path: number[]) => {
    console.log('handleCutItem called with path:', path)
    const item = getItemByPath(editedMenuItems, path)
    if (item) {
      setClipboard({
        item: JSON.parse(JSON.stringify(item)),
        mode: 'cut',
        sourcePath: path,
      })
      setMessage({ type: 'success', text: `"${item.name}" cut - paste to move` })
    }
  }

  const handlePasteItem = (targetPath: number[]) => {
    console.log('handlePasteItem called with path:', targetPath)
    console.log('Current clipboard:', clipboard)
    if (!clipboard) return

    setEditedMenuItems((prevItems) => {
      console.log('Paste - prevItems:', prevItems)
      const newItems = JSON.parse(JSON.stringify(prevItems))

      if (clipboard.mode === 'cut') {
        let current: any = newItems
        for (let i = 0; i < clipboard.sourcePath.length - 1; i++) {
          current = current[clipboard.sourcePath[i]].children
        }
        current.splice(clipboard.sourcePath[clipboard.sourcePath.length - 1], 1)
      }

      let current: any = newItems
      for (let i = 0; i < targetPath.length; i++) {
        if (i === targetPath.length - 1) {
          current = current[targetPath[i]]
        } else {
          current = current[targetPath[i]].children
        }
      }

      if (!current.children) current.children = []
      current.children.push(JSON.parse(JSON.stringify(clipboard.item)))

      console.log('Paste - newItems:', newItems)
      console.log('Paste - Target now has children:', current.children.length)
      return newItems
    })

    // Verify state update after a tick
    setTimeout(() => {
      console.log('After paste - editedMenuItems:', editedMenuItems)
    }, 100)

    setMessage({
      type: 'success',
      text: `"${clipboard.item.name}" pasted`,
    })

    if (clipboard.mode === 'cut') {
      setClipboard(null)
    }
  }

  const handleMoveUp = (path: number[]) => {
    console.log('=== MOVE UP START ===')
    console.log('Path:', path)
    console.log('Current editedMenuItems BEFORE:', JSON.stringify(editedMenuItems, null, 2))

    const itemIndex = path[path.length - 1]
    if (itemIndex === 0) {
      console.log('Already at top, cannot move up')
      return
    }

    // Get item name for message
    const item = getItemByPath(editedMenuItems, path)
    const itemName = item?.name || 'Item'

    setEditedMenuItems((prevItems) => {
      console.log('INSIDE setEditedMenuItems - prevItems:', prevItems)
      const newItems = JSON.parse(JSON.stringify(prevItems))
      console.log('Cloned newItems:', newItems)

      let current: any = newItems
      for (let i = 0; i < path.length - 1; i++) {
        console.log(`Navigating to path[${i}] = ${path[i]}`)
        current = current[path[i]].children
      }

      console.log('Current array before swap:', current)
      console.log(`Swapping index ${itemIndex} with ${itemIndex - 1}`)

      const temp = current[itemIndex]
      current[itemIndex] = current[itemIndex - 1]
      current[itemIndex - 1] = temp

      console.log('Current array after swap:', current)
      console.log('Full newItems after swap:', JSON.stringify(newItems, null, 2))
      console.log('=== MOVE UP END - RETURNING ===')
      return newItems
    })

    setMessage({ type: 'success', text: `"${itemName}" moved up` })

    // Add a small delay to check state after update
    setTimeout(() => {
      console.log(
        'editedMenuItems AFTER setState (500ms later):',
        JSON.stringify(editedMenuItems, null, 2),
      )
    }, 500)
  }

  const handleMoveDown = (path: number[]) => {
    console.log('handleMoveDown called with path:', path)

    // Get item name for message
    const item = getItemByPath(editedMenuItems, path)
    const itemName = item?.name || 'Item'

    setEditedMenuItems((prevItems) => {
      console.log('Moving down - prevItems:', prevItems)
      const newItems = JSON.parse(JSON.stringify(prevItems))
      let current: any = newItems
      for (let i = 0; i < path.length - 1; i++) {
        current = current[path[i]].children
      }
      const itemIndex = path[path.length - 1]
      if (itemIndex === current.length - 1) {
        console.log('Already at bottom, cannot move down')
        return prevItems
      }
      const temp = current[itemIndex]
      current[itemIndex] = current[itemIndex + 1]
      current[itemIndex + 1] = temp
      console.log('Moving down - newItems:', newItems)
      return newItems
    })

    setMessage({ type: 'success', text: `"${itemName}" moved down` })
  }

  const handleSaveAll = async () => {
    // Temporary checking for now
    if (
      customer &&
      customer?.email &&
      ['j.arias@templi.com', 'nick@templi.com', 'vahe@templi.com']?.includes(customer?.email)
    ) {
      setSaving(true)
      setMessage(null)
      try {
        const result = await updateMenuItems({ categories: editedMenuItems }, customer?.email)
        if (result.success) {
          setMessage({ type: 'success', text: 'Menu saved successfully!' })
          setTimeout(() => setMessage(null), 3000)
        } else {
          setMessage({ type: 'error', text: `Failed to save: ${result.error}` })
        }
      } catch (error) {
        setMessage({ type: 'error', text: 'An unexpected error occurred' })
      } finally {
        setSaving(false)
      }
    } else {
      setDisplayPrompt(true)
    }
  }

  const handleAddTopLevel = () => {
    const newItem: MegaMenuItem = {
      name: 'New Category',
      id: null,
      image_url: null,
      moq: null,
      production_time: null,
      free_shipping: null,
      isCategory: true,
      url: null,
      children: [],
    }
    setEditedMenuItems([...editedMenuItems, newItem])
  }

  if (!editedMenuItems || editedMenuItems.length === 0) {
    // Check if it's actually loading or just empty
    const isLoading = !menuItems || (Array.isArray(menuItems) && menuItems.length === 0)

    if (isLoading) {
      return (
        <div className='flex min-h-screen items-center justify-center bg-pure-white p-8'>
          <div className='text-center'>
            <CircularProgress sx={{ color: '#A95CFF' }} />
            <p className='mt-4 text-60-grey'>Loading menu items...</p>
          </div>
        </div>
      )
    }
  }

  return (
    <div className='min-h-screen bg-pure-white p-4 md:p-8'>
      <div className='mx-auto w-full max-w-7xl'>
        <div className='mb-4 md:mb-6'>
          <h1 className='mb-2 text-2xl font-bold text-tight-black md:text-3xl'>Mega Menu Editor</h1>
          <p className='text-sm text-60-grey md:text-base'>Manage navigation structure</p>
        </div>

        {/* Success/Error Snackbar */}
        {/* Custom Fixed Snackbars using Portal */}
        {(message || clipboard) &&
          typeof window !== 'undefined' &&
          ReactDOM.createPortal(
            <>
              {/* Message Snackbar - Success/Error */}
              {message && (
                <div
                  style={{
                    position: 'fixed',
                    top: '24px',
                    right: '24px',
                    zIndex: 99999,
                    animation: 'slideInFromRight 0.3s ease-out',
                  }}
                >
                  <div
                    className='flex w-[380px] items-center gap-3 rounded-lg px-4 py-3 shadow-xl'
                    style={{
                      backgroundColor: message.type === 'success' ? '#26834B' : '#DF1615',
                      color: '#FFFFFF',
                    }}
                  >
                    <Icon
                      name={message.type === 'success' ? 'check_circle' : 'error'}
                      className='flex-shrink-0 text-[24px]'
                    />
                    <span className='flex-1 text-sm font-medium'>{message.text}</span>
                    <IconButton
                      size='small'
                      onClick={() => setMessage(null)}
                      sx={{
                        color: '#FFFFFF',
                        padding: '4px',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                      }}
                    >
                      <Icon name='close' className='text-[18px]' />
                    </IconButton>
                  </div>
                </div>
              )}

              {/* Clipboard Info Snackbar */}
              {clipboard && (
                <div
                  style={{
                    position: 'fixed',
                    top: message ? '112px' : '24px',
                    right: '24px',
                    zIndex: 99998,
                    transition: 'top 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    animation: 'slideInFromRight 0.3s ease-out',
                  }}
                >
                  <div
                    className='flex w-[380px] items-center gap-3 rounded-lg border-2 px-4 py-3 shadow-xl'
                    style={{
                      backgroundColor: '#F4E9FF',
                      borderColor: '#A95CFF',
                      color: '#111111',
                    }}
                  >
                    <Icon
                      name='content_paste'
                      className='flex-shrink-0 text-[24px]'
                      style={{ color: '#A95CFF' }}
                    />
                    <span className='flex-1 text-sm font-medium'>
                      {clipboard.mode === 'copy' ? '📋 Copied' : '✂️ Cut'}: "{clipboard.item.name}"
                      <span className='mt-1 block text-xs text-60-grey'>
                        Click paste icon on target item
                      </span>
                    </span>
                    <IconButton
                      size='small'
                      onClick={() => setClipboard(null)}
                      sx={{
                        color: '#A95CFF',
                        padding: '4px',
                        '&:hover': {
                          backgroundColor: 'rgba(169, 92, 255, 0.1)',
                        },
                      }}
                    >
                      <Icon name='close' className='text-[18px]' />
                    </IconButton>
                  </div>
                </div>
              )}

              {/* Keyframes for slide animation */}
              <style>{`
              @keyframes slideInFromRight {
                from {
                  transform: translateX(100%);
                  opacity: 0;
                }
                to {
                  transform: translateX(0);
                  opacity: 1;
                }
              }
            `}</style>
            </>,
            document.body,
          )}

        <div className='mb-4 flex flex-col gap-3 md:mb-6 md:flex-row md:items-center md:justify-between'>
          <div className='flex w-full flex-col gap-2 md:w-auto md:flex-row md:gap-3'>
            <Button
              variant='contained'
              startIcon={
                saving ? (
                  <CircularProgress size={20} color='inherit' />
                ) : (
                  <Icon name='save' className='text-[14px]' />
                )
              }
              onClick={handleSaveAll}
              disabled={saving}
              fullWidth
              className='md:w-auto'
              sx={{
                border: 1,
                borderColor: '#A95CFF',
                color: '#A95CFF',
                backgroundColor: '#FFFFFF',
                '&:hover': { backgroundColor: '#884ACD', color: '#FFFFFF' },
                '&:disabled': { backgroundColor: '#939598', border: 0 },
              }}
            >
              {saving ? 'Saving...' : 'Save All'}
            </Button>

            <Button
              variant='outlined'
              startIcon={<Icon name='add' className='text-[14px]' />}
              onClick={handleAddTopLevel}
              fullWidth
              className='md:w-auto'
              sx={{
                borderColor: '#A95CFF',
                color: '#A95CFF',
                '&:hover': { borderColor: '#A95CFF', backgroundColor: '#F4E9FF' },
              }}
            >
              Add Category
            </Button>
          </div>

          <div className='text-center text-xs text-60-grey md:text-right md:text-sm'>
            {editedMenuItems.length} top-level items
          </div>
        </div>

        <div className='space-y-2'>
          {editedMenuItems.map((item, index) => (
            <MenuItemEditor
              key={`root-${index}-${item.name}`}
              item={item}
              path={[index]}
              onUpdate={handleUpdateItem}
              onDelete={handleDeleteItem}
              onAddChild={handleAddChild}
              onCopy={handleCopyItem}
              onCut={handleCutItem}
              onPaste={handlePasteItem}
              onMoveUp={handleMoveUp}
              onMoveDown={handleMoveDown}
              level={0}
              isLast={index === editedMenuItems.length - 1}
              isFirst={index === 0}
              hasClipboard={!!clipboard}
            />
          ))}
        </div>

        {editedMenuItems.length === 0 && (
          <div className='rounded-lg border border-20-grey bg-pure-white p-12 text-center'>
            <p className='mb-4 text-60-grey'>No menu items</p>
            <Button
              variant='contained'
              startIcon={<Icon name='add' className='text-[14px]' />}
              onClick={handleAddTopLevel}
              sx={{
                backgroundColor: '#A95CFF',
                '&:hover': { backgroundColor: '#884ACD' },
              }}
            >
              Create First Category
            </Button>
          </div>
        )}
      </div>
      <AlertPrompt
        title='Invalid Action'
        message='You must be logged in to perform this action.'
        isDisplayed={displayPrompt}
        setIsDisplayed={setDisplayPrompt}
      />
    </div>
  )
}
